/* src/Pages/SlotsPage/components/SlotTable.tsx */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table, Input, Button, Tag, message, Select, DatePicker, Pagination } from 'antd';
import { shouldExpectAsyncRank } from '../../../Utils/slotRankPolling';
import { getSlotRankDisplayText } from '../../../Utils/slotRankDisplay';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import SlotRankModal from './SlotRankModal';
import dayjs from 'dayjs';

import { Slot, PaginationInfo } from '../../../Modules/Slot/Model/Slot';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { isSlotOperationRestricted } from '../../../Utils/timeRestriction';
import { getSlotPermissions } from '../../../Utils/slotPermissions';

interface Props {
  data: Slot[];
  loading: boolean;
  selected: Key[];
  onSelectChange: (ids: Key[]) => void;
  onInlineUpdate: (id: number, dto: Partial<Slot>) => void;
  pagination?: PaginationInfo;
  onPageChange?: (page: number, pageSize?: number) => void;
  localFindQuery: string;
  activeLocalFindMatchIndex: number;
  onLocalFindMatchCountChange: (count: number) => void;
  /** 페이지 내 찾기로 테이블 스크롤 후 검색 입력 포커스 복구용 */
  onLocalFindScrollApplied?: () => void;
}

interface SlotRow extends Slot {
  __base: Slot;
  __draft?: Partial<Slot>;
  __editing: boolean;
  __completed: boolean;
}

interface DebouncedInputProps {
  value: string;
  onDebouncedChange: (value: string) => void;
  status?: '' | 'error' | 'warning';
}

interface TableMatch {
  slotId: number;
  rowIndex: number;
}

interface TableScrollApi {
  scrollTo?: (config: { index?: number; key?: Key; top?: number }) => void;
}

const DebouncedInput: React.FC<DebouncedInputProps> = React.memo(({ value, onDebouncedChange, status }) => {
  const [inner, setInner] = useState(value);

  useEffect(() => {
    setInner(value);
  }, [value]);

  useEffect(() => {
    const t = setTimeout(() => {
      onDebouncedChange(inner);
    }, 90);
    return () => clearTimeout(t);
  }, [inner, onDebouncedChange]);

  return (
    <Input
      value={inner}
      status={status}
      onChange={(e) => setInner(e.target.value)}
      onBlur={() => onDebouncedChange(inner)}
    />
  );
});

/* 상태별 태그 색 */
const colorMap: Record<Slot['status'], string> = {
  정상: 'green',
  연장: 'green',
  환불: 'red',
  종료: 'orange',
};

/* 빈값이면 자동 편집을 유발하는 필드 */
const blankCheckFields: (keyof Slot)[] = [
  'main_keyword',
  'product_url',
];

const dateFormat = 'YYYY-MM-DD';
const VIRTUAL_ROW_THRESHOLD = 100;
const TABLE_BODY_HEIGHT = 560;
const TABLE_SCROLL_X = 2200;
const FALLBACK_ROW_HEIGHT = 44;
const remainingZeroStyle: React.CSSProperties = {
  backgroundColor: '#ffefef',
  fontWeight: 'bold',
  color: '#d9534f',
  padding: '2px 6px',
  borderRadius: 4,
  textAlign: 'center',
};

function stripLiftSuffix(username?: string | null): string {
  if (!username) return '';
  return username.endsWith('@lift') ? username.slice(0, -5) : username;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const SlotTable: React.FC<Props> = ({
  data,
  loading,
  selected,
  onSelectChange,
  onInlineUpdate,
  pagination,
  onPageChange,
  localFindQuery,
  activeLocalFindMatchIndex,
  onLocalFindMatchCountChange,
  onLocalFindScrollApplied,
}) => {

  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const isAdmin = currentUser?.grade === 'ADMIN';

  // 계정 id -> username(또는 username(등급)) 맵을 미리 만들어 셀 렌더링에서 O(1) 조회
  const accountIdMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of accounts) map.set(a.id, a.username);
    // 내 계정은 selector로 들어온 값을 우선(서버 accounts에 없을 수도 있어서)
    if (currentUser?.id) map.set(currentUser.id, currentUser.username);
    return map;
  }, [accounts, currentUser?.id, currentUser?.username]);

  const accountIdMapWithGrade = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of accounts) map.set(a.id, `${a.username} (${a.grade})`);
    if (currentUser?.id) map.set(currentUser.id, currentUser.username);
    return map;
  }, [accounts, currentUser?.id, currentUser?.username]);

  const getUsernameById = useCallback((id: number | null | undefined, showGrade: boolean) => {
    if (!id) return '-';
    return (showGrade ? accountIdMapWithGrade : accountIdMap).get(id) ?? '-';
  }, [accountIdMap, accountIdMapWithGrade]);

  // 시간 제한 확인
  const isRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;

  const sellers = useMemo(() => 
    accounts
      .filter(a => a.grade === 'SELLER')
      .sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  // Select 옵션 JSX를 미리 만들어 셀마다 sellers.map을 반복하지 않도록 캐싱
  const sellerOptionNodes = useMemo(() => (
    sellers.map(s => (
      <Select.Option
        key={s.id}
        value={s.id}
        label={`${s.username} (${s.name})`}
      >
        {s.username} ({s.name})
      </Select.Option>
    ))
  ), [sellers]);

  /* URL 검사 */
  const isValidUrl = useCallback((url?: string) => {
    if (!url) return false;
    try { new URL(url); return true; } catch { return false; }
  }, []);

  /* 편집 상태 관리 */
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
  const [completedRows, setCompletedRows] = useState<Set<number>>(new Set());
  const [drafts, setDrafts] = useState<Record<number, Partial<Slot>>>({});
  const draftsRef = useRef<Record<number, Partial<Slot>>>({});
  const rowCacheRef = useRef<Map<number, SlotRow>>(new Map());
  
  /* 순위 모달 상태 */
  const [rankModalVisible, setRankModalVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const tableRef = useRef<TableScrollApi | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // NOTE: rank_history는 테이블에서 처리하지 않습니다. (today_rank만 사용)

  /* ───── 빈칸 자동 편집 트리거 ───── */
  useEffect(() => {
    const pendingIds = new Set(
      data
        .filter((slot) => slot._optimistic)
        .map((slot) => slot.slotId),
    );
    const idsNeedingEdit = data
      .filter(
        s =>
          !pendingIds.has(s.slotId) && /* 생성 저장 중에는 편집하지 않음 */
          s.status !== '환불' && /* ✅ 환불 슬롯은 스킵 */
          s.status !== '종료' && /* ✅ 종료 슬롯은 스킵 */
          blankCheckFields.some(f => {
            const v = s[f];
            return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
          }),
      )
      .map(s => s.slotId);

    setEditingRows(prev => {
      const next = new Set(prev);
      pendingIds.forEach(id => next.delete(id));
      idsNeedingEdit.forEach(id => { if (!completedRows.has(id)) next.add(id); });
      return next;
    });
  }, [data, completedRows]);

  // 슬롯별 권한을 한 번 계산해서 캐싱 (반복 계산 방지)
  const slotPermissionsById = useMemo(() => {
    const map = new Map<number, ReturnType<typeof getSlotPermissions>>();
    for (const s of data) {
      map.set(s.slotId, getSlotPermissions(s, currentUser, isRestricted));
    }
    return map;
  }, [data, currentUser, isRestricted]);

  const getPermissions = useCallback((r: Slot) => {
    return slotPermissionsById.get(r.slotId) ?? getSlotPermissions(r, currentUser, isRestricted);
  }, [slotPermissionsById, currentUser, isRestricted]);

  /* 편집 가능 여부 */
  const isEditing = useCallback((r: SlotRow) => {
    const permissions = slotPermissionsById.get(r.slotId);
    return !!permissions?.canEdit && !r._optimistic && r.__editing;
  }, [slotPermissionsById]);

  /* 현재 값(드래프트 우선) */
  const getVal = <K extends keyof Slot>(row: SlotRow, key: K): Slot[K] | '' =>
    row.__draft?.[key] ?? row[key] ?? '';
  const statusColumnWidth = (pagination?.page_size ?? data.length) >= 100 ? 72 : 40;
  const localFindKeyword = localFindQuery.trim();
  const normalizedLocalFindQuery = localFindKeyword.toLowerCase();

  const getCreatorDisplayText = useCallback((row: Slot) => {
    const id = row.manager ?? row.creator;
    if (!id) return '-';
    if (id === 13) return 'adpa99';
    if (!row.manager && row.creator_username) {
      return stripLiftSuffix(row.creator_username);
    }
    return getUsernameById(id, false);
  }, [getUsernameById]);

  const getUserDisplayText = useCallback((row: Slot) => {
    return stripLiftSuffix(row.user_username) || getUsernameById(row.user, false);
  }, [getUsernameById]);

  const getRankDisplayText = useCallback((row: Slot) => getSlotRankDisplayText(row), []);

  const getLocalFindSearchText = useCallback((row: Slot) => (
    [
      row.slotId,
      row.slot_number,
      row.status,
      row.status === '연장' ? '정상' : '',
      getCreatorDisplayText(row),
      getUserDisplayText(row),
      row.manager,
      row.creator,
      row.user,
      getRankDisplayText(row),
      row.today_rank,
      row.yesterday_rank,
      row.rank_check_status,
      row.remaining_days,
      row.start_date,
      row.end_date,
      row.main_keyword,
      row.word_keyword,
      row.mid_single,
      row.mid_compare,
      row.product_url,
      row.compare_url,
      row.memo,
    ]
      .filter(value => value !== null && value !== undefined && value !== '')
      .join(' ')
      .toLowerCase()
  ), [getCreatorDisplayText, getRankDisplayText, getUserDisplayText]);

  const renderLocalFindText = useCallback((value: string | number | null | undefined) => {
    if (value === null || value === undefined) return value ?? '-';

    const text = String(value);
    if (!text) return '-';
    if (!localFindKeyword || !normalizedLocalFindQuery) return text;

    const parts = text.split(new RegExp(`(${escapeRegExp(localFindKeyword)})`, 'ig'));
    if (parts.length === 1) return text;

    return parts.map((part, index) => (
      part.toLowerCase() === normalizedLocalFindQuery ? (
        <mark key={`${text}-${index}`} className="slot-local-find-match">
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${text}-${index}`}>{part}</React.Fragment>
      )
    ));
  }, [localFindKeyword, normalizedLocalFindQuery]);

  const tableData = useMemo<SlotRow[]>(() => {
    const cache = rowCacheRef.current;
    const nextIds = new Set<number>();

    const rows = data.map((row) => {
      nextIds.add(row.slotId);
      const draft = drafts[row.slotId];
      const editing = editingRows.has(row.slotId);
      const completed = completedRows.has(row.slotId);

      const prev = cache.get(row.slotId);
      const same =
        !!prev &&
        prev.__base === row &&
        prev.__draft === draft &&
        prev.__editing === editing &&
        prev.__completed === completed;

      if (same) return prev;

      const nextRow: SlotRow = {
        ...row,
        __base: row,
        __draft: draft,
        __editing: editing,
        __completed: completed,
      };
      cache.set(row.slotId, nextRow);
      return nextRow;
    });

    for (const id of Array.from(cache.keys())) {
      if (!nextIds.has(id)) cache.delete(id);
    }

    return rows;
  }, [data, drafts, editingRows, completedRows]);

  const localFindMatches = useMemo<TableMatch[]>(() => {
    if (!normalizedLocalFindQuery) return [];

    return data.reduce<TableMatch[]>((acc, row, rowIndex) => {
      if (getLocalFindSearchText(row).includes(normalizedLocalFindQuery)) {
        acc.push({ slotId: row.slotId, rowIndex });
      }
      return acc;
    }, []);
  }, [data, getLocalFindSearchText, normalizedLocalFindQuery]);

  const activeLocalFindMatch = useMemo(
    () => (
      activeLocalFindMatchIndex >= 0
        ? (localFindMatches[activeLocalFindMatchIndex] ?? null)
        : null
    ),
    [activeLocalFindMatchIndex, localFindMatches],
  );

  /** 객체 참조가 매 입력마다 바뀌지 않도록 스크롤 effect 의존성을 원시값으로만 둠 */
  const activeLocalFindScrollKey = useMemo(() => {
    if (activeLocalFindMatchIndex < 0) return '';
    const m = localFindMatches[activeLocalFindMatchIndex];
    if (!m) return '';
    return `${m.slotId}:${m.rowIndex}`;
  }, [activeLocalFindMatchIndex, localFindMatches]);

  const scrollToLocalFindMatch = useCallback((match: TableMatch) => {
    const tableApi = tableRef.current;
    if (tableApi?.scrollTo) {
      tableApi.scrollTo({ key: match.slotId });
      return;
    }

    const holder = tableWrapperRef.current?.querySelector(
      '.ant-table-tbody-virtual-holder, .ant-table-body',
    ) as HTMLElement | null;
    if (!holder) return;

    const firstRenderedRow = tableWrapperRef.current?.querySelector(
      'tr.ant-table-row',
    ) as HTMLElement | null;
    const rowHeight = firstRenderedRow?.getBoundingClientRect().height || FALLBACK_ROW_HEIGHT;
    holder.scrollTop = match.rowIndex * rowHeight;
  }, []);

  useEffect(() => {
    onLocalFindMatchCountChange(localFindMatches.length);
  }, [localFindMatches.length, onLocalFindMatchCountChange]);

  useEffect(() => {
    if (!activeLocalFindScrollKey) return;
    const colon = activeLocalFindScrollKey.indexOf(':');
    if (colon <= 0) return;
    const slotId = Number(activeLocalFindScrollKey.slice(0, colon));
    const rowIndex = Number(activeLocalFindScrollKey.slice(colon + 1));
    if (!Number.isFinite(slotId) || !Number.isFinite(rowIndex)) return;
    const match: TableMatch = { slotId, rowIndex };

    const timer = window.setTimeout(() => {
      scrollToLocalFindMatch(match);
      onLocalFindScrollApplied?.();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [activeLocalFindScrollKey, scrollToLocalFindMatch, onLocalFindScrollApplied]);

  const shouldCellUpdate = useCallback((record: SlotRow, prevRecord: SlotRow) => {
    return record !== prevRecord;
  }, []);

  const setRowDraft = useCallback((slotId: number, patch: Partial<Slot>) => {
    const current = draftsRef.current;
    const next = {
      ...current,
      [slotId]: { ...(current[slotId] ?? {}), ...patch },
    };
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const clearRowDraft = useCallback((slotId: number) => {
    const current = draftsRef.current;
    const next = { ...current, [slotId]: {} };
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const updateDraftField = useCallback((slotId: number, key: keyof Slot, value: any) => {
    setRowDraft(slotId, { [key]: value } as Partial<Slot>);
  }, [setRowDraft]);

  /* ───── 컬럼 정의 ───── */
  const rawColumns: ColumnsType<SlotRow> = useMemo(() => [
    {
      title: '슬롯 번호',
      dataIndex: 'slotId',
      width: 85,
      align: 'center',
      render: (value: number) => renderLocalFindText(value),
    },

    {
      title: '상태',
      dataIndex: 'status',
      width: statusColumnWidth,
      render: (v: Slot['status']) => {
        const displayText = v === '연장' ? '정상' : v;
        return <Tag color={colorMap[v]}>{renderLocalFindText(displayText)}</Tag>;
      },
    },
    {
      title: '생성자',
      dataIndex: 'manager',
      width: 90,
              render: (_, rec) => {
          const id = rec.manager ?? rec.creator;
          if (!id) return '-';
          
          // 특정 ID에 대한 매핑
          if (id === 13) return renderLocalFindText('adpa99');

          // 목록 응답에 creator_username이 있으면 그대로 사용 (accounts 매핑 비용 감소)
          if (!rec.manager && rec.creator_username) {
            return renderLocalFindText(stripLiftSuffix(rec.creator_username));
          }
          
          return renderLocalFindText(getUsernameById(id, false));
        },
    },
    {
      title: '아이디',
      dataIndex: 'user',
      width: (pagination?.page_size ?? data.length) >= 100 ? 200 : 90, // 100개 이상일 때 2배 가까이 늘림
      align: 'center',
      render: (id: number, r) =>
        isEditing(r) ? (
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            optionLabelProp="label"
            value={getVal(r, 'user') as number | undefined}
            onChange={val =>
              setRowDraft(r.slotId, { user: val })
            }
          >
            {sellerOptionNodes}
          </Select>
        ) : (
          // 목록 응답에 user_username이 있으면 그대로 사용 (accounts 매핑 비용 감소)
          renderLocalFindText(stripLiftSuffix(r.user_username) || getUsernameById(id, false))
        ),
    },
    {
      title: '가격 비교 순위',
      dataIndex: 'rank_history',
      width: 130,
      align: 'center' as const,
      // 오늘 완료면 today_rank(+NEW), 아니면 yesterday_rank 우선, 둘 다 없으면 상태 문구
      render: (_: any, record: SlotRow) => {
        const handleRankClick = () => {
          setSelectedSlot(record);
          setRankModalVisible(true);
        };

        const rankText = getRankDisplayText(record);
        const hasNoTarget = !shouldExpectAsyncRank(record);
        const isTodayChecked = record.rank_check_status === 'complete';
        const showingYesterday =
          !isTodayChecked &&
          record.yesterday_rank !== null &&
          record.yesterday_rank !== undefined;
        const showingStatus =
          !hasNoTarget &&
          !isTodayChecked &&
          !showingYesterday;
        const displayedRank = isTodayChecked
          ? record.today_rank
          : showingYesterday
            ? record.yesterday_rank
            : null;
        const isMuted =
          hasNoTarget ||
          showingStatus ||
          displayedRank === null ||
          displayedRank === undefined ||
          displayedRank > 500;

        return (
          <span
            style={{
              color: isMuted ? '#999' : undefined,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              gap: 2,
              lineHeight: 1.2,
            }}
            onClick={handleRankClick}
          >
            <span>{renderLocalFindText(rankText)}</span>
            {isTodayChecked && (
              <span
                style={{
                  fontSize: 8,
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: 0,
                  color: '#b03052',
                  marginTop: 1,
                  flexShrink: 0,
                }}
              >
                NEW
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: '남은일수',
      dataIndex: 'remaining_days',
      width: 85,
      align: 'center',
      render: (v: number) =>
        v === 0 ? (
          <div style={remainingZeroStyle}>
            {renderLocalFindText(v)}
          </div>
        ) : renderLocalFindText(v),
    },
    {
      title: '시작일',
      dataIndex: 'start_date',
      width: 110,
      render: (v, r) =>
        isEditing(r) && isAdmin ? (
          <DatePicker
            style={{ width: '100%' }}
            value={(() => {
              const val = getVal(r, 'start_date') as string | '';
              return val ? dayjs(val, dateFormat) : null;
            })()}
            disabledDate={(d) => {
              const end = getVal(r, 'end_date') as string | '';
              return end ? d.isAfter(dayjs(end, dateFormat), 'day') : false;
            }}
            onChange={(d) =>
              setRowDraft(r.slotId, { start_date: d ? d.format(dateFormat) : '' })
            }
            format={dateFormat}
          />
        ) : (
          renderLocalFindText(v)
        ),
    },
    /* ───── 관리 버튼 (시작일 다음) ───── */
    {
      title: '수정',
      dataIndex: 'manage',
      width: 80,
      align: 'center' as const,
      onCell: () => ({ style: { verticalAlign: 'middle' } }),
      render: (_, r) => {
        const permissions = getPermissions(r);
        const isPending = !!r._optimistic;

        if (isPending) {
          return (
            <Button size="small" disabled>
              대기 중
            </Button>
          );
        }

        if (isEditing(r)) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                size="small"
                type="primary"
                onClick={async () => {
                  const dto = draftsRef.current[r.slotId] || r.__draft || {};
                  const urlInvalid =
                    (dto.product_url && !isValidUrl(dto.product_url)) ||
                    (dto.compare_url && !isValidUrl(dto.compare_url));
                  if (urlInvalid) { message.error('올바른 URL 형식이 아닙니다.'); return; }

                  try {
                    await onInlineUpdate(r.slotId, dto);
                    setEditingRows(s => { const n = new Set(s); n.delete(r.slotId); return n; });
                    setCompletedRows(s => new Set(s).add(r.slotId));
                    clearRowDraft(r.slotId);
                  } catch (error) {
                    // no-op
                  }
                }}
              >
                완료
              </Button>
            </div>
          );
        }

        if (r.__completed) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                size="small"
                disabled={!permissions.canEdit}
                onClick={() => {
                  setCompletedRows(s => { const n = new Set(s); n.delete(r.slotId); return n; });
                  setEditingRows(s => new Set(s).add(r.slotId));
                }}
              >
                수정
              </Button>
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              size="small"
              disabled={!permissions.canEdit}
              onClick={() => setEditingRows(s => new Set(s).add(r.slotId))}
            >
              수정
            </Button>
          </div>
        );
      },
    },
    {
      title: '종료일',
      dataIndex: 'end_date',
      width: 110,
      render: (v, r) =>
        isEditing(r) && isAdmin ? (
          <DatePicker
            style={{ width: '100%' }}
            value={(() => {
              const val = getVal(r, 'end_date') as string | '';
              return val ? dayjs(val, dateFormat) : null;
            })()}
            disabledDate={(d) => {
              const start = getVal(r, 'start_date') as string | '';
              return start ? d.isBefore(dayjs(start, dateFormat), 'day') : false;
            }}
            onChange={(d) =>
              setRowDraft(r.slotId, { end_date: d ? d.format(dateFormat) : '' })
            }
            format={dateFormat}
          />
        ) : (
          renderLocalFindText(v)
        ),
    },

    /* ───── 편집 가능 필드들 ───── */
    {
      title: '메인키워드',
      dataIndex: 'main_keyword',
      render: (v, r) =>
        isEditing(r) ? (
          <DebouncedInput
            value={String(getVal(r, 'main_keyword') ?? '')}
            onDebouncedChange={(v) => updateDraftField(r.slotId, 'main_keyword', v)}
          />
        ) : renderLocalFindText(v),
    },
    {
      title: '단일 MID',
      dataIndex: 'mid_single',
      width: 120,
      render: (v, r) =>
        isEditing(r) ? (
          <DebouncedInput
            value={String(getVal(r, 'mid_single') ?? '')}
            onDebouncedChange={(v) => updateDraftField(r.slotId, 'mid_single', v)}
          />
        ) : renderLocalFindText(v),
    },
    {
      title: '가격비교 MID',
      dataIndex: 'mid_compare',
      width: 120,
      render: (v, r) =>
        isEditing(r) ? (
          <DebouncedInput
            value={String(getVal(r, 'mid_compare') ?? '')}
            onDebouncedChange={(v) => updateDraftField(r.slotId, 'mid_compare', v)}
          /> 
        ) : renderLocalFindText(v),
    },
    {
      title: '상품 URL',
      dataIndex: 'product_url',
      render: (v, r) =>
        isEditing(r) ? (
          <DebouncedInput
            value={String(getVal(r, 'product_url') ?? '')}
            status={getVal(r, 'product_url') && !isValidUrl(getVal(r, 'product_url') as string) ? 'error' : ''}
            onDebouncedChange={(v) => updateDraftField(r.slotId, 'product_url', v)}
          />
        ) : renderLocalFindText(v || '-'),
    },
    {
      title: '가격비교 URL',
      dataIndex: 'compare_url',
      render: (v, r) =>
        isEditing(r) ? (
          <DebouncedInput
            value={String(getVal(r, 'compare_url') ?? '')}
            status={getVal(r, 'compare_url') && !isValidUrl(getVal(r, 'compare_url') as string) ? 'error' : ''}
            onDebouncedChange={(v) => updateDraftField(r.slotId, 'compare_url', v)}
          />
        ) : renderLocalFindText(v || '-'),
    },
    {
      title: '메모',
      dataIndex: 'memo',
      render: (v, r) =>
        isEditing(r) ? (
          <DebouncedInput
            value={String(getVal(r, 'memo') ?? '')}
            onDebouncedChange={(v) => updateDraftField(r.slotId, 'memo', v)}
          />
        ) : renderLocalFindText(v),
    },

    /* (기존 고정 열의 관리 버튼은 제거됨) */
  ], [currentUser, isRestricted, isAdmin, onInlineUpdate, isEditing, getPermissions, isValidUrl, getUsernameById, getRankDisplayText, sellerOptionNodes, updateDraftField, getVal, setRowDraft, clearRowDraft, statusColumnWidth, renderLocalFindText]);

  const columns = useMemo<ColumnsType<SlotRow>>(
    () => rawColumns.map((col) => ({ ...col, shouldCellUpdate })),
    [rawColumns, shouldCellUpdate]
  );

  // 테이블 변경 핸들러
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      // 페이지 변경만 처리
      if (pagination.current !== undefined) {
        onPageChange?.(pagination.current, pagination.pageSize || 20);
      }
    },
    [onPageChange],
  );

  // 페이지네이션 설정
  const paginationProps = pagination ? {
    current: pagination.current_page || 1,
    pageSize: pagination.page_size || 20,
    total: pagination.total_count || 0,
  } : undefined;

  const rowSelection = useMemo(
    () => ({
      type: 'checkbox' as const,
      columnWidth: 44,
      selectedRowKeys: selected,
      onChange: onSelectChange,
      getCheckboxProps: (record: SlotRow) => ({
        disabled: !!record._optimistic,
      }),
    }),
    [selected, onSelectChange]
  );
  const useVirtualTable = tableData.length >= VIRTUAL_ROW_THRESHOLD;
  const tableScroll = useMemo(
    () =>
      useVirtualTable
        ? { x: TABLE_SCROLL_X, y: TABLE_BODY_HEIGHT }
        : { x: 'max-content' as const },
    [useVirtualTable]
  );

  const rowClassName = useCallback(
    (record: SlotRow) => (
      normalizedLocalFindQuery && record.slotId === activeLocalFindMatch?.slotId
        ? 'slot-table-local-find-active-row'
        : ''
    ),
    [activeLocalFindMatch?.slotId, normalizedLocalFindQuery],
  );

  return (
    <>
      <div ref={tableWrapperRef} className="slot-table" style={{ width: '100%', overflowX: 'auto' }}>
      <Table
        ref={tableRef as any}
        size="small"
        rowKey="slotId"
        dataSource={tableData}
        columns={columns}
        loading={loading}
        bordered
        rowSelection={rowSelection}
        rowClassName={rowClassName}
        virtual={useVirtualTable}
        className="slot-table-local-find"
        style={{ minWidth: 950 }}
        scroll={tableScroll}
        pagination={false}
        onChange={handleTableChange}
      />
      </div>

      {paginationProps && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
          <Pagination
            current={paginationProps.current}
            pageSize={paginationProps.pageSize}
            total={paginationProps.total}
            showSizeChanger
            pageSizeOptions={['50','100','200','300','400','500','1000']}
            showQuickJumper={false}
            showLessItems
            responsive
            showTotal={(total: number, range: [number, number]) => `${range[0]}-${range[1]} / ${total.toLocaleString()}개 슬롯`}
            onChange={(page, pageSize) => onPageChange?.(page, pageSize)}
            onShowSizeChange={(_, size) => onPageChange?.(1, size)}
          />
        </div>
      )}

        <SlotRankModal
          visible={rankModalVisible}
          slot={selectedSlot}
          onClose={() => {
            setRankModalVisible(false);
            setSelectedSlot(null);
          }}
        />
    </>
  );
};

export default React.memo(SlotTable);
